import { useState, useSyncExternalStore } from "react";
import { Link, useLocation } from "react-router-dom";

import { AbcFitnessBrandLogo } from "@/components/portal/AbcFitnessBrandLogo";
import { GlofoxBrandLogo } from "@/components/portal/GlofoxBrandLogo";
import { GlofoxDocSearch } from "@/components/portal/GlofoxDocSearch";
import { GlofoxSidebarNav } from "@/components/portal/GlofoxSidebarNav";
import {
  MOBILE_NAV_DRAWER_ID,
  MobileNavDrawer,
} from "@/components/portal/MobileNavDrawer";
import { SkipToContent } from "@/components/portal/SkipToContent";
import { UserAccountMenu } from "@/components/portal/UserAccountMenu";
import { useColorTheme } from "@/hooks/useColorTheme";
import {
  getAuthSessionSnapshot,
  getContactFirstName,
  subscribeAuthSession,
} from "@/lib/auth/session";
import { useSandboxCredentialsEnabled } from "@/lib/feature-flags/useSandboxCredentialsEnabled";
import {
  GLOFOX_API_REFERENCE_ROUTE,
  GLOFOX_DOCS_HOME_ROUTE,
} from "@/lib/portal/docs-urls";
import {
  getGlofoxLegacyApiReferenceHref,
  GLOFOX_REQUEST_ACCESS_DOCS_ROUTE,
  GLOFOX_REQUEST_ACCESS_HREF,
  resolveGlofoxTopNavActive,
} from "@/lib/portal/glofox-routes";
import {
  PORTAL_HOME_ROUTE,
  PORTAL_LANDING_ROUTE,
  PORTAL_LOGIN_LEGACY_ROUTE,
  PORTAL_SIGNUP_LEGACY_ROUTE,
  PORTAL_SIGNUP_ROUTE,
} from "@/lib/portal/portal-routes";

export function GlofoxNav() {
  const { pathname } = useLocation();
  const slice6Enabled = useSandboxCredentialsEnabled();
  const showLegacyExperience = !slice6Enabled;
  // These pages carry no toggle, so they clear a dark theme left by a form.
  useColorTheme({ darkModeEnabled: slice6Enabled });
  const requestAccessHref = showLegacyExperience
    ? GLOFOX_REQUEST_ACCESS_DOCS_ROUTE
    : GLOFOX_REQUEST_ACCESS_HREF;
  const {
    docs: docsActive,
    apiReference: apiReferenceActive,
    requestAccess: requestAccessActive,
  } = resolveGlofoxTopNavActive(pathname);
  const navLinkColor = (active: boolean) =>
    active ? "var(--gf)" : "rgba(255,255,255,.6)";
  const [menuState, setMenuState] = useState({ pathname, open: false });
  if (menuState.pathname !== pathname) {
    setMenuState({ pathname, open: false });
  }
  const menuOpen = menuState.open;
  const session = useSyncExternalStore(
    subscribeAuthSession,
    getAuthSessionSnapshot,
    getAuthSessionSnapshot,
  );
  const displayName = session ? getContactFirstName(session.user.contact_name) : null;

  function closeMenu() {
    setMenuState((prev) => (prev.open ? { ...prev, open: false } : prev));
  }

  function toggleMenu() {
    setMenuState((prev) => ({ ...prev, open: !prev.open }));
  }

  const authBlock = displayName ? (
    <UserAccountMenu
      userName={displayName}
      testIdPrefix="gf-nav"
      variant="portal"
    />
  ) : (
    <>
      <Link
        to={
          showLegacyExperience ? PORTAL_LOGIN_LEGACY_ROUTE : PORTAL_HOME_ROUTE
        }
        className="btn btn-outline portal-topnav__auth-btn"
        data-testid="gf-nav-login"
        onClick={closeMenu}
      >
        <i className="ti ti-login" aria-hidden />
        <span className="portal-topnav__auth-label">Log in</span>
      </Link>
      <Link
        to={
          showLegacyExperience ? PORTAL_SIGNUP_LEGACY_ROUTE : PORTAL_SIGNUP_ROUTE
        }
        className="btn btn-outline portal-topnav__auth-btn"
        data-testid="gf-nav-signup"
        onClick={closeMenu}
      >
        <i className="ti ti-user-plus" aria-hidden />
        <span className="portal-topnav__auth-label">Sign up</span>
      </Link>
    </>
  );

  return (
    <>
      <SkipToContent />
      <nav
      className="topnav portal-topnav"
      data-testid="gf-nav"
      aria-label="Glofox developer portal"
    >
      <div className="portal-topnav__left">
        <button
          type="button"
          className="portal-topnav__menu-toggle"
          data-testid="gf-nav-menu-toggle"
          aria-expanded={menuOpen}
          aria-controls={MOBILE_NAV_DRAWER_ID}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          onClick={toggleMenu}
        >
          <i className={`ti ${menuOpen ? "ti-x" : "ti-menu-2"}`} aria-hidden />
        </button>

        <div className="portal-topnav__brand">
          <AbcFitnessBrandLogo
            to={PORTAL_LANDING_ROUTE}
            theme="dark"
            testId="gf-nav-logo"
          />
          <span className="portal-topnav__sep" aria-hidden>
            /
          </span>
          <GlofoxBrandLogo to={GLOFOX_DOCS_HOME_ROUTE} testId="gf-nav-glofox" />
        </div>

        <ul className="nav-links portal-topnav__desktop-links">
          <li>
            <Link
              to={GLOFOX_DOCS_HOME_ROUTE}
              style={{ color: navLinkColor(showLegacyExperience && docsActive) }}
              aria-current={
                showLegacyExperience && docsActive ? "page" : undefined
              }
              data-testid="gf-nav-docs"
            >
              Docs
            </Link>
          </li>
          <li>
            {showLegacyExperience ? (
              <a
                href={getGlofoxLegacyApiReferenceHref()}
                style={{ color: navLinkColor(apiReferenceActive) }}
                aria-current={apiReferenceActive ? "page" : undefined}
                data-testid="gf-nav-api-reference"
              >
                API Reference
              </a>
            ) : (
              <Link
                to={GLOFOX_API_REFERENCE_ROUTE}
                style={{ color: "rgba(255,255,255,.6)" }}
                data-testid="gf-nav-api-reference"
              >
                API Reference
              </Link>
            )}
          </li>
          {showLegacyExperience ? (
            <li>
              <Link
                to={requestAccessHref}
                style={{
                  color: requestAccessActive ? "var(--gf)" : "rgba(255,255,255,.6)",
                }}
                aria-current={requestAccessActive ? "page" : undefined}
                data-testid="gf-nav-request-access"
              >
                Request access
              </Link>
            </li>
          ) : null}
        </ul>
      </div>

      <div className="portal-topnav__right nav-right">
        {showLegacyExperience ? <GlofoxDocSearch /> : null}
        <div className="portal-topnav__auth portal-topnav__desktop-auth">
          {authBlock}
        </div>
      </div>

      <MobileNavDrawer
        open={menuOpen}
        onClose={closeMenu}
        footer={
          <div className="mobile-nav-drawer__auth" data-testid="gf-nav-drawer-auth">
            {displayName ? (
              <UserAccountMenu
                userName={displayName}
                testIdPrefix="gf-nav-drawer"
                variant="portal"
              />
            ) : (
              <>
                <Link
                  to={
                    showLegacyExperience
                      ? PORTAL_LOGIN_LEGACY_ROUTE
                      : PORTAL_HOME_ROUTE
                  }
                  className="btn btn-outline portal-topnav__auth-btn"
                  data-testid="gf-nav-drawer-login"
                  onClick={closeMenu}
                >
                  <i className="ti ti-login" aria-hidden />
                  <span>Log in</span>
                </Link>
                <Link
                  to={
                    showLegacyExperience
                      ? PORTAL_SIGNUP_LEGACY_ROUTE
                      : PORTAL_SIGNUP_ROUTE
                  }
                  className="btn btn-outline portal-topnav__auth-btn"
                  data-testid="gf-nav-drawer-signup"
                  onClick={closeMenu}
                >
                  <i className="ti ti-user-plus" aria-hidden />
                  <span>Sign up</span>
                </Link>
              </>
            )}
          </div>
        }
      >
        <ul className="mobile-nav-drawer__links">
          <li>
            <Link
              to={GLOFOX_DOCS_HOME_ROUTE}
              className={`mobile-nav-drawer__link${
                showLegacyExperience && docsActive ? " is-active" : ""
              }`}
              aria-current={
                showLegacyExperience && docsActive ? "page" : undefined
              }
              data-testid="gf-nav-drawer-docs"
              onClick={closeMenu}
            >
              Docs
            </Link>
          </li>
          <li>
            {showLegacyExperience ? (
              <a
                href={getGlofoxLegacyApiReferenceHref()}
                className={`mobile-nav-drawer__link${
                  apiReferenceActive ? " is-active" : ""
                }`}
                aria-current={apiReferenceActive ? "page" : undefined}
                data-testid="gf-nav-drawer-api-reference"
                onClick={closeMenu}
              >
                API Reference
              </a>
            ) : (
              <Link
                to={GLOFOX_API_REFERENCE_ROUTE}
                className="mobile-nav-drawer__link"
                data-testid="gf-nav-drawer-api-reference"
                onClick={closeMenu}
              >
                API Reference
              </Link>
            )}
          </li>
          {showLegacyExperience ? (
            <li>
              <Link
                to={requestAccessHref}
                className={`mobile-nav-drawer__link${
                  requestAccessActive ? " is-active" : ""
                }`}
                aria-current={requestAccessActive ? "page" : undefined}
                data-testid="gf-nav-drawer-request-access"
                onClick={closeMenu}
              >
                Request access
              </Link>
            </li>
          ) : null}
        </ul>
        {showLegacyExperience ? (
          <div
            className="mobile-nav-drawer__docs"
            data-testid="gf-nav-drawer-docs-nav"
          >
            <GlofoxSidebarNav testIdPrefix="gf-drawer" onNavigate={closeMenu} />
          </div>
        ) : null}
      </MobileNavDrawer>
    </nav>
    </>
  );
}
